import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Users, Mail, Phone, RefreshCw, Share2, Gift } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const RANK_STYLES = {
  1: { bg: "bg-purple-600", text: "text-purple-50", emoji: "👑" },
  2: { bg: "bg-fuchsia-500", text: "text-fuchsia-50", emoji: "🥈" },
  3: { bg: "bg-pink-500", text: "text-pink-50", emoji: "🥉" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

export const TopReferrersWidget = () => {
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // {referral_code: bool}
  const [rewardingId, setRewardingId] = useState(null);
  const [rewardResult, setRewardResult] = useState(null);

  const fetchTop = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dashboard/top-referrers?limit=10`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setReferrers(r.data?.referrers || []);
    } catch (e) {
      console.error("[top-referrers] fetch failed:", e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTop();
  }, [fetchTop]);

  const rewardReferrer = useCallback(
    async (referrer) => {
      if (!referrer.customer_id || !referrer.email) return;
      const ok = window.confirm(
        `Genera coupon -10% per ${referrer.name || referrer.email}?\n\n` +
          `Riceverà un'email automatica con il codice univoco (valido 60 giorni, uso singolo).`
      );
      if (!ok) return;
      setRewardingId(referrer.customer_id);
      setRewardResult(null);
      try {
        const r = await axios.post(
          `${API}/dashboard/reward-referrer`,
          { customer_id: referrer.customer_id, discount_percent: 10, validity_days: 60 },
          { headers: getAuthHeaders(), withCredentials: true }
        );
        setRewardResult({
          customer_id: referrer.customer_id,
          code: r.data.code,
          emailSent: r.data.email_sent,
          name: referrer.name || referrer.email,
        });
        // Auto-dismiss success banner after 8s
        setTimeout(() => setRewardResult(null), 8000);
      } catch (e) {
        const detail = e?.response?.data?.detail || e.message;
        alert(`Errore: ${detail}`);
      } finally {
        setRewardingId(null);
      }
    },
    []
  );

  const totalReferrals = referrers.reduce((s, r) => s + (r.referrals_count || 0), 0);

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="top-referrers-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-400" />
            Top 10 Ambassador
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchTop}
            disabled={loading}
            className="h-7 px-2 text-gray-300 hover:text-white"
            data-testid="top-referrers-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </CardTitle>
        {referrers.length > 0 ? (
          <p className="text-xs text-gray-400">
            <span className="text-purple-300 font-bold">{totalReferrals}</span> nuovi clienti
            invitati da questi {referrers.length} ambassador
          </p>
        ) : (
          <p className="text-xs text-gray-500">
            Clienti che hanno portato nuovi iscritti tramite link referral
          </p>
        )}
      </CardHeader>
      <CardContent>
        {rewardResult && (
          <div
            className="mb-3 p-3 bg-emerald-900/40 border border-emerald-700 rounded-lg text-sm"
            data-testid="reward-result-banner"
          >
            <div className="flex items-center gap-2 text-emerald-200 font-semibold">
              <Gift className="w-4 h-4" />
              Coupon -10% inviato a {rewardResult.name}
            </div>
            <div className="mt-1 text-xs text-emerald-300">
              Codice: <span className="font-mono">{rewardResult.code}</span>
              {rewardResult.emailSent
                ? " · Email inviata ✓"
                : " · ⚠️ Email non recapitata (verifica SMTP)"}
            </div>
          </div>
        )}
        {loading && referrers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">Caricamento...</div>
        ) : referrers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Nessun referral attivo ancora.<br />
            <span className="text-[11px]">I clienti che condividono prodotti generano referral.</span>
          </div>
        ) : (
          <div className="space-y-2">
            {referrers.map((r, idx) => {
              const rank = idx + 1;
              const style = RANK_STYLES[rank];
              const isExpanded = expanded[r.referral_code];
              return (
                <div
                  key={r.referral_code || idx}
                  className="bg-gray-900/60 rounded-lg p-3 border border-gray-700 hover:border-gray-600 transition-colors"
                  data-testid={`top-referrer-row-${rank}`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
                        style ? `${style.bg} ${style.text}` : "bg-gray-700 text-gray-300"
                      }`}
                    >
                      {style ? style.emoji : `#${rank}`}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-white truncate">
                          {r.name || "(account chiuso)"}
                        </p>
                        {r.referral_code && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-300 font-mono">
                            {r.referral_code}
                          </span>
                        )}
                      </div>
                      {r.email && (
                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5 truncate">
                          <a
                            href={`mailto:${r.email}`}
                            className="hover:text-blue-400 flex items-center gap-1 truncate"
                          >
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{r.email}</span>
                          </a>
                          {r.phone && (
                            <a
                              href={`tel:${r.phone}`}
                              className="hover:text-emerald-400 hidden md:flex items-center gap-1"
                            >
                              <Phone className="w-3 h-3" />
                              {r.phone}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-bold text-purple-300">
                        {r.referrals_count}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        {r.referrals_count === 1 ? "invitato" : "invitati"}
                      </p>
                      {r.sample_referrals?.length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setExpanded((s) => ({ ...s, [r.referral_code]: !isExpanded }))
                          }
                          className="text-[10px] text-purple-400 hover:text-purple-300 mt-0.5"
                          data-testid={`top-referrer-expand-${rank}`}
                        >
                          {isExpanded ? "nascondi" : "vedi"}
                        </button>
                      )}
                      {/* Reward button — generate -10% coupon + email it */}
                      {r.customer_id && r.email && (
                        <button
                          type="button"
                          onClick={() => rewardReferrer(r)}
                          disabled={rewardingId === r.customer_id}
                          className="block mt-1 text-[10px] px-2 py-0.5 rounded bg-emerald-700/40 hover:bg-emerald-700 text-emerald-200 hover:text-white border border-emerald-700/60 disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
                          data-testid={`reward-referrer-${rank}`}
                          title="Genera coupon -10% e invia per email"
                        >
                          {rewardingId === r.customer_id ? "..." : "🎁 -10%"}
                        </button>
                      )}
                    </div>
                  </div>
                  {isExpanded && r.sample_referrals?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-700 space-y-1">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                        Ultimi invitati
                      </p>
                      {r.sample_referrals.map((s, sIdx) => (
                        <div
                          key={s.email || s.joined || `${r.referral_code}-${sIdx}`}
                          className="text-xs text-gray-400 flex justify-between"
                        >
                          <span className="truncate">
                            {s.name || s.email?.split("@")[0]}
                          </span>
                          <span className="text-gray-500 ml-2 flex-shrink-0">
                            {formatDate(s.joined)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {referrers.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-700">
            <p className="text-[11px] text-gray-500 text-center">
              💡 Premia i tuoi top ambassador con sconti esclusivi per amplificare il word-of-mouth
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
